import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Badge, Stack } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLocationDot, faHeart, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { theme } from '../../config/theme';
import { toast } from 'react-toastify';
import { LanguageProvider } from '../../core/language-provider';

export default function JobDetail() {
    const { blue, lightBlue } = theme;
    const { k } = useParams();
    const id = parseInt(k)
    const navigate = useNavigate();

    const [job, setJob] = useState(null);
    const [favId, setFavId] = useState(null); // favourite record id if exists
    const [hasApplied, setHasApplied] = useState(false);

    useEffect(() => {
        getJob()
    }, [id])


    async function getJob() {
        const res = await DataManager.getItem('job_post', id);
        console.log(res);
        if (res.success === 1) {
            const item = Array.isArray(res.body) ? res.body[0] : res.body;
            setJob(item);
            // After job loaded, check favourite
            await checkFavourite(item?.id || id);
            await checkApplied(item?.id || id);
        } else {
            toast.error("Errore nel caricare la posizione");
        }
    }

    async function checkFavourite(jobId) {
        try {
            const user = LoginManager.getUserInfo();
            if (!user?.id || !jobId) { setFavId(null); return; }
            const sorting = { field: 'id', order: 'desc' };
            const constraints = [
                { fieldName: 'idUser', type: 'value', value: user.id },
                { fieldName: 'idJobPost', type: 'value', value: jobId }
            ];
            const res = await DataManager.searchItems('job_post_favourites', 1, 1, sorting, '', [], constraints);
            const rec = (res.success === 1 && Array.isArray(res.body) && res.body[0]) ? res.body[0] : null;
            setFavId(rec?.id || null);
        } catch (err) {
            console.error('checkFavourite error', err);
            setFavId(null);
        }
    }

    async function toggleFavourite(jobId) {
        const user = LoginManager.getUserInfo();
        if (!user?.id) {
            navigate('/login', { state: { redirect: `/job/${id}` } });
            return;
        }
        try {
            if (favId) {
                const del = await DataManager.deleteItem('job_post_favourites', favId);
                if (del.success === 1) setFavId(null);
                return;
            }
            const ins = await DataManager.insertItem('job_post_favourites', {
                idUser: { id: user.id },
                idJobPost: { id: jobId }
            });
            if (ins.success === 1) {
                // fetch created favourite id
                const sorting = { field: 'id', order: 'desc' };
                const constraints = [
                    { fieldName: 'idUser', type: 'value', value: user.id },
                    { fieldName: 'idJobPost', type: 'value', value: jobId }
                ];
                const find = await DataManager.searchItems('job_post_favourites', 1, 1, sorting, '', [], constraints);
                const rec = (find.success === 1 && Array.isArray(find.body) && find.body[0]) ? find.body[0] : null;
                if (rec?.id) setFavId(rec.id);
            }
        } catch (err) {
            console.error('toggleFavourite error', err);
        }
    }

    async function checkApplied(jobId) {
        try {
            const user = LoginManager.getUserInfo();
            if (!user?.id || !jobId) { setHasApplied(false); return; }
            const sorting = { field: 'id', order: 'desc' };
            const constraints = [
                { fieldName: 'idUser', type: 'value', value: user.id },
                { fieldName: 'idJobPost', type: 'value', value: jobId }
            ];
            const res = await DataManager.searchItems('job_request', 1, 1, sorting, '', [], constraints);
            const exists = (res.success === 1 && Array.isArray(res.body) && res.body.length > 0);
            setHasApplied(exists);
        } catch (err) {
            console.error('checkApplied error', err);
            setHasApplied(false);
        }
    }

    async function verifyOpenAndProceed() {
        if (hasApplied) return;
        // Re-fetch job to ensure latest status before proceeding
        const res = await DataManager.getItem('job_post', id);
        if (res && res.success === 1) {
            const item = Array.isArray(res.body) ? res.body[0] : res.body;
            setJob(item);
            const isOpen = item?.status.value === '10_OPEN';
            if (!isOpen) {
                toast.warning(LanguageProvider.get('pages.jobDetail.notOpen') || 'La posizione non è più aperta');
                return;
            }
        }

        const logged = await LoginManager.checkLogin();
        if (logged) {
            navigate(`/job/${id}/apply`);
        } else {
            navigate('/login', { state: { redirect: `/job/${id}/apply` } });
        }
    }


    const tags = useMemo(() => (job?.tags ? job.tags.map(t => (t.name || t.title || t)) : []), [job]);
    const title = job?.title || job?.name || '';
    const country = job?.idLocation?.country || job?.country || '';
    const city = job?.idLocation?.city || job?.city || '';
    const address = job?.idLocation?.address || [city, country].filter(Boolean).join(', ');

    const sections = useMemo(() => {
        if (!job) return [];
        const arr = [];
        if (job.description) arr.push({ label: LanguageProvider.get('pages.jobDetail.description'), content: job.description, isHtml: true });
        if (job.idLocation) arr.push({ label: LanguageProvider.get('pages.jobDetail.workplace'), content: job.idLocation.city });
        if (job.idCountry) arr.push({ label: LanguageProvider.get('pages.jobDetail.country'), content: job.idCountry.name });
        // Ruolo nascosto su richiesta
        // if (job.idRole) arr.push({ label: LanguageProvider.get('pages.jobDetail.role'), content: job.idRole.name });
        if (job.idSector) arr.push({ label: LanguageProvider.get('pages.jobDetail.sector'), content: job.idSector.name });
        return arr;
    }, [job]);

    return (
        <Container fluid>
            <Container fluid={'md'}>
                {/* Title + actions */}
                <Row className='align-items-center pt-5 pb-3 g-3'>
                    <Col md={8} xs={12}>
                        {/* Mobile: back + title on left, heart at end */}
                        <div className='d-flex align-items-center justify-content-between d-md-none'>
                            <div className='d-flex align-items-center'>
                                <button type='button' onClick={() => navigate(-1)} className='btn btn-link p-0 me-2' style={{ color: blue, textDecoration: 'none' }}>
                                    <FontAwesomeIcon icon={faArrowLeft} className='me-1' />
                                    <span className='visually-hidden'>{LanguageProvider.get('pages.jobDetail.back')}</span>
                                </button>
                                <h2 className='job-detail-title mb-0' style={{ color: blue }}>{title}</h2>
                            </div>
                            <Button
                                variant='outline-light'
                                className='btn-fav ms-3'
                                onClick={() => toggleFavourite(job?.id || id)}
                                style={{ border: '1px solid #e6edf5' }}
                            >
                                <FontAwesomeIcon icon={faHeart} style={{ color: favId ? '#E91E63' : '#000', opacity: favId ? 1 : 0.6 }} />
                            </Button>
                        </div>
                        {/* Desktop: back + title inline and centered vertically */}
                        <div className='d-none d-md-flex align-items-center'>
                            <button type='button' onClick={() => navigate(-1)} className='btn btn-link p-0 me-3' style={{ color: blue, textDecoration: 'none' }}>
                                <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
                                <span className='visually-hidden'>{LanguageProvider.get('pages.jobDetail.back')}</span>
                            </button>
                            <h2 className='job-detail-title mb-0' style={{ color: blue }}>{title}</h2>
                        </div>
                    </Col>
                    <Col md={4} xs={12} className='job-detail-actions d-flex flex-column flex-md-row align-items-center justify-content-md-end text-center gap-2 gap-md-3 mt-2 mt-md-0'>
                        <Button
                            variant='outline-light'
                            className='btn-fav d-none d-md-inline-flex'
                            onClick={() => toggleFavourite(job?.id || id)}
                            style={{ border: '1px solid #e6edf5' }}
                        >
                            <FontAwesomeIcon icon={faHeart} style={{ color: favId ? '#E91E63' : '#000', opacity: favId ? 1 : 0.6 }} />
                        </Button>
                        <Button
                            className='btn-apply'
                            style={{ backgroundColor: lightBlue, borderColor: lightBlue }}
                            disabled={hasApplied || (job?.status && job.status.value !== '10_OPEN')}
                            onClick={verifyOpenAndProceed}
                        >
                            {hasApplied ? LanguageProvider.get('pages.jobDetail.appliedAlready') : (<>
                                {LanguageProvider.get('pages.jobDetail.applyNow')} <FontAwesomeIcon icon={faArrowRight} className='ms-2' />
                            </>)}
                        </Button>
                    </Col>
                </Row>

                {/* Location */}
                <Row className='mb-3'>
                    <Col>
                        <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ color: lightBlue, textTransform: 'uppercase', textDecoration: 'none' }}
                            aria-label={`Apri in Google Maps: ${address}`}
                        >
                            <FontAwesomeIcon icon={faLocationDot} /> {country}{country && city ? ', ' : ''}{city}
                        </a>
                    </Col>
                </Row>

                {/* Tags */}
                <Row className='mb-4 job-detail-tags'>
                    <Col>
                        <Stack direction='horizontal' gap={2} className='flex-wrap'>
                            {tags.map((t, i) => (
                                <Badge key={i} bg='' style={{ border: '1px solid #dbe7f2', color: blue, backgroundColor: '#f2f7fb', fontWeight: 500 }}>{t}</Badge>
                            ))}
                        </Stack>
                    </Col>
                </Row>

                {/* Description and details */}
                <Row className='mb-5'>
                    <Col lg={8} md={10}>
                        {sections.map((sec, i) => (
                            <div key={i} className={`job-detail-section ${i === 0 ? '' : 'mt-4'}`}>
                                <h6 style={{ color: blue }}>{sec.label}</h6>
                                {sec.isHtml ? (
                                    <div className='text-muted mb-0' dangerouslySetInnerHTML={{ __html: sec.content }} />
                                ) : Array.isArray(sec.content) ? (
                                    <ul className='text-muted mb-0'>
                                        {sec.content.map((li, idx) => <li key={idx}>{li}</li>)}
                                    </ul>
                                ) : (
                                    <p className='text-muted mb-0'>{sec.content}</p>
                                )}
                            </div>
                        ))}
                    </Col>
                </Row>

                <Row className='pb-5'>
                    <Col lg={8} md={10}>
                        <Button
                            className='btn-apply'
                            style={{ backgroundColor: lightBlue, borderColor: lightBlue }}
                            disabled={hasApplied || (job?.status && job.status.value !== '10_OPEN')}
                            onClick={verifyOpenAndProceed}
                        >
                            {hasApplied ? LanguageProvider.get('pages.jobDetail.appliedAlready') : (<>
                                {LanguageProvider.get('pages.jobDetail.applyNow')} <FontAwesomeIcon icon={faArrowRight} className='ms-2' />
                            </>)}
                        </Button>
                    </Col>
                </Row>
            </Container>
        </Container>
    );
}
